import type {
	JiraSideNav,
	JiraSideNavMessageMetadata,
} from '@post-office/placement-create-pipeline/pipes';
import {
	createClientPipeline,
	createServerPipeline,
} from '@post-office/placement-create-pipeline/create-pipeline';
import { type GenerateValidationFor } from '@atlassian/vindicated';

import { type MessageTemplateContext } from '../../..';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';
import { type AnyHydrationResult } from '@post-office/shared-contracts/placement-pipe/pipeline-types';
import { type RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context';
import { type JSX } from 'react/jsx-runtime';

export type HydrationResult = Record<string, never>;

export type VHydrationResult = GenerateValidationFor<HydrationResult>;

type Pipe = JiraSideNav<{
	messageContext: MessageTemplateContext;
	additionalRenderProps: {
		onDismissed: () => void;
	};
	hydrationResult: HydrationResult;
}>;

const clientPipeline = createClientPipeline<Pipe>();
export const createRender: <
	U extends (renderProps: { onDismissed: () => void }) => JSX.Element | null,
>(
	render: U,
) => (renderProps: { onDismissed: () => void }) => JSX.Element | null = clientPipeline.createRender;
export const createValidate: (
	validate: (apiResponse: unknown) => HydrationResult,
) => (apiResponse: unknown) => HydrationResult = clientPipeline.createValidate;
const serverPipeline = createServerPipeline<Pipe>();
export const createHydrate: (
	hydrate: (
		requestContext: RequestContext,
		placementContext: Record<string, never>,
		messageContext: {
			testProp: string;
			testObjProp: {
				testObjPropKey: string;
			};
			testArrayProp: number[];
		} & {
			messageInstanceId: string;
			eventTime: string;
		},
	) => Promise<HydrationResult>,
) => (
	requestContext: RequestContext,
	placementContext: Record<string, never>,
	messageContext: {
		testProp: string;
		testObjProp: {
			testObjPropKey: string;
		};
		testArrayProp: number[];
	} & {
		messageInstanceId: string;
		eventTime: string;
	},
) => Promise<AnyHydrationResult> = serverPipeline.createHydrate;
export const createHydrateExample: (
	hydrateExample: (
		requestContext: Partial<RequestContext>,
		placementContext: Record<string, never>,
		messageContext: {
			testProp: string;
			testObjProp: {
				testObjPropKey: string;
			};
			testArrayProp: number[];
		},
	) => HydrationResult,
	config: {
		requestContext?: Partial<RequestContext>;
		additionalRenderProps: {
			onDismissed: () => void;
		};
	},
) => (
	requestContext: Partial<RequestContext>,
	placementContext: Record<string, never>,
	messageContext: {
		testProp: string;
		testObjProp: {
			testObjPropKey: string;
		};
		testArrayProp: number[];
	} & {
		messageInstanceId: string;
		eventTime: string;
	},
) => {
	onDismissed: () => void;
} = serverPipeline.createHydrateExample;
export const createTransformMetadata: (
	transformMetadata: (params: {
		messageContext: {
			testProp: string;
			testObjProp: {
				testObjPropKey: string;
			};
			testArrayProp: number[];
		};
	}) => JiraSideNavMessageMetadata,
) => (params: {
	messageContext: {
		testProp: string;
		testObjProp: {
			testObjPropKey: string;
		};
		testArrayProp: number[];
	};
}) => TransformMetadataResult = serverPipeline.createTransformMetadata;
